import React, { useEffect, useState } from 'react';
import {
  Box, Button, Link, Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Add as AddIcon, InfoOutlined } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import useStyles from './styles';
import {
  DEFAULT_STUDY_PLAN_TUITION,
  StudentContract,
  StudentProgram,
  StudentRelevantInfo,
} from './types';
import { ReduxState } from '../../types';
import {
  ADMINISTRATOR, SECRETARY, STUDENT, REPRESENTATIVE,
} from '../../utils/user_types';
import {
  getEnrollmentSummaryByStudentId,
  getStudentContractsByStudentId,
  putStudyPlan,
  postContract,
} from '../../requests/api/students';
import { getPaymentDocumentsByStudentId } from '../../requests/api/contractPayment';
import { PostContractData } from '../Student/StudentListView/types';
import { ACCEPTED, DRAFT } from '../../utils/contract';
import ProgramCard from './component/ProgramCard';
import RelevantInfo from './component/RelevantInfo';
import { getSubjects } from '../../requests/api/subjects';
import Discounts from './component/Discounts';
import AddProgramModal from './component/AddProgramModal';
import EmptyStudyPlanMessage from './component/EmptyStudyPlanMessage';
import { ProgramModalData } from './component/AddProgramModal/types';
import handleApiResponse from '../../utils/handleApiResponse';
import AssignSectionModal from './component/AssignSectionModal';
import DeleteConfirmModal from './component/DeleteConfirmModal';
import { StudentStage } from '../../utils/enrollment';

interface StudyPlanProps {
  /** Id del alumno. Necesario cuando se renderiza desde /alumnos/:id (apoderado). */
  studentIdProp?: string;
  /** Stage del alumno para ocultar "Pagar Matrícula" cuando ya está inscrito. */
  studentStage?: string | null;
}

function StudyPlan({ studentIdProp, studentStage }: StudyPlanProps = {}) {
  const classes = useStyles();
  const params = useParams<{ studentId?: string; id?: string }>();
  const studentId = studentIdProp ?? params.id ?? params.studentId ?? '';
  const isEnrolled = studentStage === StudentStage.ENROLLED || studentStage === StudentStage.PAID;
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector((state: ReduxState) => state.account);
  const [programs, setPrograms] = useState<StudentProgram[]>([]);
  const [contracts, setContracts] = useState<StudentContract[]>([]);
  const [studentRelevantInfo, setStudentRelevantInfo] = useState<StudentRelevantInfo | null>(null);
  const [subjects, setSubjects] = useState<Array<{ id: string; name: string }>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [assignSectionModalOpen, setAssignSectionModalOpen] = useState(false);
  const [selectedStudentProgram, setSelectedStudentProgram] = useState<StudentProgram | null>(null);
  const [editingProgram, setEditingProgram] = useState<ProgramModalData | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<StudentProgram | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hasPendingTuitionPaymentDocument, setHasPendingTuitionPaymentDocument] = useState(false);

  // Permisos de usuario
  const canManagePrograms = account?.user?.userType === ADMINISTRATOR
    || account?.user?.userType === SECRETARY;
  const isNotStudent = account?.user?.userType !== STUDENT;
  const isStudentOrRepresentative = account?.user?.userType === STUDENT
    || account?.user?.userType === REPRESENTATIVE;

  // Una vez que el apoderado acepta, la pestaña queda informativa: los cambios se hacen
  // desde el contrato del alumno (pestaña Alumnos), validados por finanzas y soporte.
  const isPlanAccepted = contracts[0]?.status === ACCEPTED;
  const canEditPlan = canManagePrograms && !isPlanAccepted;
  const mustAcceptPlan = account?.user?.userType === REPRESENTATIVE
    && Boolean(contracts[0])
    && !isPlanAccepted;

  const fetchContracts = async () => {
    if (!studentId) return;
    const response = await getStudentContractsByStudentId(studentId);
    const fetchedContracts = response.studentContracts.contracts || [];
    setContracts(fetchedContracts);
  };

  useEffect(() => {
    fetchContracts();
  }, [studentId]);

  useEffect(() => {
    const allPrograms: StudentProgram[] = [];
    contracts.forEach((contract) => {
      contract.studentPrograms?.forEach((program) => {
        allPrograms.push(program);
      });
    });
    setPrograms(allPrograms);
  }, [contracts]);

  useEffect(() => {
    const fetchStudentRelevantInfo = async () => {
      if (!studentId) {
        return;
      }

      try {
        const [enrollmentData, subjectsData] = await Promise.all([
          getEnrollmentSummaryByStudentId(studentId),
          getSubjects(),
        ]);

        if (isNotStudent) {
          try {
            const paymentDocumentsData = await getPaymentDocumentsByStudentId(studentId);
            setHasPendingTuitionPaymentDocument(paymentDocumentsData.exists || false);
          } catch (paymentErr) {
            const paymentError = paymentErr as AxiosError;
            if (paymentError.response?.status !== 401 && paymentError.response?.status !== 403) {
              handleApiResponse(enqueueSnackbar, paymentError, false);
            }
            setHasPendingTuitionPaymentDocument(false);
          }
        }

        const subjectsList = subjectsData.subjects || [];
        setSubjects(subjectsList.map((s: { id: string; name: string }) => ({
          id: s.id,
          name: s.name,
        })));

        const testAreas = enrollmentData.student?.testAreas || [];
        const testsComment = enrollmentData.student?.comment || '';
        const inscriptionYear = enrollmentData.student?.inscriptionYear;
        const locationName = enrollmentData.student?.location || '';
        const classYear = enrollmentData.student?.classYear || '';
        const leadSource = enrollmentData.student?.leadSource || '';
        const leadName = enrollmentData.student?.leadName || '';
        const ambassadorCode = enrollmentData.student?.ambassadorCode || '';
        const schoolName = (() => {
          const school = enrollmentData.student?.school || '';
          const otherSchool = enrollmentData.student?.otherSchoolName;
          if (school.toLowerCase() === 'otro' && otherSchool) {
            return `Otro (${otherSchool})`;
          }
          return school;
        })();

        const testsToTake = testAreas.map((subjectName: string) => {
          const subject = subjectsList.find(
            (s: { id: string; name: string }) => s.name === subjectName,
          );
          return {
            id: subject ? Number(subject.id) : 0,
            name: subjectName,
          };
        }).filter((test: { id: number; name: string }) => test.id > 0);

        setStudentRelevantInfo({
          testsToTake,
          testsComment,
          locationName,
          inscriptionYear,
          classYear,
          schoolName,
          relationToStudent: leadSource,
          leadName,
          ambassadorCode,
        });
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
    };
    fetchStudentRelevantInfo();
  }, [studentId]);

  const handleOpenAssignSectionModal = (studentProgram: StudentProgram) => {
    setSelectedStudentProgram(studentProgram);
    setAssignSectionModalOpen(true);
  };

  const handleCloseAssignSectionModal = () => {
    setAssignSectionModalOpen(false);
    setSelectedStudentProgram(null);
  };

  const handleSectionAssigned = async () => {
    await fetchContracts();
  };

  const handleOpenEditProgramModal = (studentProgram: StudentProgram) => {
    // Convertir StudentProgram a ProgramModalData para edición
    const programData: ProgramModalData = {
      subjectId: String(studentProgram.program.subject.id),
      subjectName: studentProgram.program.subject.name,
      year: new Date().getFullYear(), // Año actual por defecto
      level: '', // Dejamos vacío, se puede mejorar extrayendo del nombre
      classYear: '', // Dejamos vacío, se puede mejorar extrayendo del nombre
      programId: String(studentProgram.programId),
      programName: studentProgram.program.name,
      startDate: new Date(studentProgram.program.startDate),
      endDate: new Date(studentProgram.program.endDate),
      originalFee: String(studentProgram.program.fee),
      discount: String(studentProgram.feeDiscount),
      comment: studentProgram.comment || '',
    };
    setEditingProgram(programData);
    setModalOpen(true);
  };

  const handleOpenDeleteModal = (studentProgram: StudentProgram) => {
    setProgramToDelete(studentProgram);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setProgramToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!programToDelete || contracts.length === 0) {
      return;
    }

    setDeleteLoading(true);
    const currentContract = contracts[0];

    // Filtrar el programa a eliminar
    const updatedProgramInfo = (currentContract.studentPrograms || [])
      .filter((sp) => sp.programId !== programToDelete.programId)
      .map((sp) => ({
        programId: String(sp.programId),
        feeDiscount: sp.feeDiscount,
        comment: sp.comment || '',
      }));

    try {
      await putStudyPlan({
        contractId: String(currentContract.id),
        studentId,
        startDate: new Date(currentContract.startDate),
        endDate: new Date(currentContract.endDate),
        tuition: currentContract.tuition,
        tuitionDiscount: currentContract.tuitionDiscount,
        tuitionAgreement: currentContract.tuitionAgreement,
        feeDiscount: currentContract.feeDiscount || 0,
        feeAdditionalDiscount: currentContract.feeAdditionalDiscount || 0,
        feePaymentTypeDiscount: currentContract.feePaymentTypeDiscount || 0,
        feeDiscountInvalidPaymentMethods: currentContract.feeDiscountInvalidPaymentMethods,
        feeDiscountValidDays: currentContract.feeDiscountValidDays,
        studentProgramInfo: updatedProgramInfo,
      });

      enqueueSnackbar('Programa eliminado exitosamente', { variant: 'success' });
      await fetchContracts();
      handleCloseDeleteModal();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddProgram = async (programData: ProgramModalData) => {
    // Si no hay contrato, crear uno automáticamente
    if (contracts.length === 0 && !editingProgram) {
      try {
        // Crear contrato con el primer programa
        const contractData: PostContractData = {
          studentId,
          startDate: programData.startDate,
          endDate: programData.endDate,
          tuition: DEFAULT_STUDY_PLAN_TUITION,
          tuitionDiscount: 0,
          tuitionAgreement: null,
          tuitionPaymentType: '',
          feeDiscountType: null,
          feeDiscount: 0,
          feeAdditionalDiscount: 0,
          feePaymentType: '',
          feePaymentTypeDiscount: 0,
          studentProgramInfo: [{
            programId: programData.programId,
            feeDiscount: Number(programData.discount),
            comment: programData.comment || '',
          }],
          comment: '',
          installments: [],
          status: DRAFT,
          academicYear: programData.year,
        };

        await postContract(contractData);
        enqueueSnackbar('Contrato creado y programa agregado exitosamente', { variant: 'success' });

        // Recargar los contratos para actualizar la vista
        await fetchContracts();
        setEditingProgram(null);
        return;
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
        return;
      }
    }

    if (contracts.length === 0) {
      enqueueSnackbar('No hay un contrato activo para editar programas', {
        variant: 'error',
      });
      return;
    }

    const currentContract = contracts[0];

    // Obtener los programas existentes del contrato
    const existingPrograms = currentContract.studentPrograms?.map((sp) => ({
      programId: String(sp.programId),
      feeDiscount: sp.feeDiscount,
      comment: sp.comment || '',
    })) || [];

    let updatedProgramInfo;
    let successMessage;

    if (editingProgram) {
      // Modo edición: actualizar el programa existente
      updatedProgramInfo = existingPrograms.map((p) => {
        if (p.programId === programData.programId) {
          return {
            programId: programData.programId,
            feeDiscount: Number(programData.discount),
            comment: programData.comment || '',
          };
        }
        return p;
      });
      successMessage = 'Programa actualizado exitosamente';
    } else {
      // Modo agregar: agregar nuevo programa
      const newProgram = {
        programId: programData.programId,
        feeDiscount: Number(programData.discount),
        comment: programData.comment || '',
      };
      updatedProgramInfo = [...existingPrograms, newProgram];
      successMessage = 'Programa agregado exitosamente';
    }

    try {
      await putStudyPlan({
        contractId: String(currentContract.id),
        studentId,
        startDate: new Date(currentContract.startDate),
        endDate: new Date(currentContract.endDate),
        tuition: currentContract.tuition,
        tuitionDiscount: currentContract.tuitionDiscount,
        tuitionAgreement: currentContract.tuitionAgreement,
        feeDiscount: currentContract.feeDiscount || 0,
        feeAdditionalDiscount: currentContract.feeAdditionalDiscount || 0,
        feePaymentTypeDiscount: currentContract.feePaymentTypeDiscount || 0,
        feeDiscountInvalidPaymentMethods: currentContract.feeDiscountInvalidPaymentMethods,
        feeDiscountValidDays: currentContract.feeDiscountValidDays,
        studentProgramInfo: updatedProgramInfo,
      });

      enqueueSnackbar(successMessage, { variant: 'success' });

      // Recargar los contratos para actualizar la vista
      await fetchContracts();
      setEditingProgram(null);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const shouldShowEmptyMessage = isStudentOrRepresentative
    && (account?.user?.userType === REPRESENTATIVE || account?.user?.stage)
    && programs.length === 0;

  return (
    <Box className={classes.root}>
      {canManagePrograms && (
        <RelevantInfo studentRelevantInfo={studentRelevantInfo || undefined} />
      )}
      {canManagePrograms && isPlanAccepted && (
        <Box className={classes.infoCard}>
          <InfoOutlined className={classes.infoCardIcon} />
          <Typography className={classes.infoCardText}>
            Los cambios al plan de estudios deben realizarse desde la pestaña{' '}
            <Link
              component={RouterLink}
              to={`/alumnos/${studentId}`}
              className={classes.infoCardLink}
            >Alumnos</Link>, editando el contrato correspondiente.
            <strong> Antes de realizar cualquier cambio, este debe ser validado
            por Finanzas y Soporte Académico.</strong>
          </Typography>
        </Box>
      )}
      {mustAcceptPlan && !shouldShowEmptyMessage && (
        <Box className={classes.infoCard}>
          <InfoOutlined className={classes.infoCardIcon} />
          <Typography className={classes.infoCardText}>
            Para continuar con la inscripción debes aceptar el plan de estudios.
            Revisa los programas, horarios y valores antes de aceptarlo.
          </Typography>
        </Box>
      )}
      {shouldShowEmptyMessage ? (
        <EmptyStudyPlanMessage />
      ) : (
        <Box className={isNotStudent ? classes.mainContentContainer : classes.studentMainContainer}>
          <Box className={isNotStudent ? classes.leftSide : classes.programsStudentContainer}>
            {programs.map((program) => (
              <ProgramCard
                key={program.id}
                studentProgram={program}
                showPrices={true}
                onAssignSection={canEditPlan ? handleOpenAssignSectionModal : undefined}
                onEditProgram={canEditPlan ? handleOpenEditProgramModal : undefined}
                onDeleteProgram={canEditPlan ? handleOpenDeleteModal : undefined}
              />
            ))}
            {canEditPlan && (
              <Button
                variant="contained"
                fullWidth
                color="primary"
                startIcon={<AddIcon />}
                className={classes.fullWidthButton}
                onClick={() => setModalOpen(true)}
              >
                Agregar Programa
              </Button>
            )}
            <AddProgramModal
              modalOpen={modalOpen}
              setModalOpen={(open) => {
                setModalOpen(open);
                if (!open) {
                  setEditingProgram(null);
                }
              }}
              onAddProgram={handleAddProgram}
              editingProgram={editingProgram}
              subjects={subjects}
            />
            <DeleteConfirmModal
              open={deleteModalOpen}
              onClose={handleCloseDeleteModal}
              onConfirm={handleConfirmDelete}
              programName={programToDelete?.program.name || ''}
              loading={deleteLoading}
            />
            <AssignSectionModal
              open={assignSectionModalOpen}
              onClose={handleCloseAssignSectionModal}
              studentId={studentId}
              programId={selectedStudentProgram?.programId.toString() || ''}
              programFee={selectedStudentProgram?.program.fee}
              locationId={null}
              onSectionAssigned={handleSectionAssigned}
            />
          </Box>
          <Box className={isNotStudent ? classes.rightSide : classes.commentButtonStudentContainer}>
            <Discounts
              contract={contracts[0]}
              studentId={studentId}
              onContractUpdated={fetchContracts}
              isEnrolled={isEnrolled}
              hasPendingTuitionPaymentDocument={hasPendingTuitionPaymentDocument}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default StudyPlan;
